<template>
    <div v-click-outside="closeDropdown" class="dropdown" :class="[disabled ? 'dropdown--disabled' : '']" v-bind="$attrs">
        <button 
            type="button" 
            :class="[
                btnClasses, 
                `button--${size}`, 
                `dropdown-btn-${size}`, 
                `${readonly ? 'disabled' : 
                    ''}`
            ]" 
            class="btn button--outline__secondary dropdown-btn" 
            :disabled="outerLoading"
            @click="toggleDropdown"
        >
            <span v-if="reactiveSelected">
                <template v-if="typeof reactiveSelected === 'string'">
                    <div>
                        <span>{{ reactiveSelected }}</span>
                    </div>
                </template>

                <template v-if="typeof reactiveSelected === 'object'">
                    <template v-if="!multiple">
                        <div>
                            <span>{{ computeOptionItem(reactiveSelected) }}</span>
                        </div>
                    </template>

                    <template v-else>
                        <div class="dropdown--selection">
                            <span v-for="item in reactiveSelected" :key="getUniqueId(item)" class="dropdown--item--multiple">
                                <span class="dropdown--item-desc">{{ computeOptionItem(item) }}</span>
                                <span class="cursor-pointer inline-block" @click.stop="$emit('remove', item)">
                                    <IconSvg name="close" width="16px" fill="#ced4da" />
                                </span>
                            </span>
                        </div>
                    </template>
                </template> 

                <template v-else>
                    <div>
                        <span>{{ placeholder }}</span>
                    </div>
                </template>
            </span>

            <template v-else>
                <p>{{ placeholder }}</p>
            </template>
            
            <span class="cursor-pointer inline-block dropdown-btn-icon">
                <div v-if="outerLoading">
                    <PageSpinner
                        size="sm" 
                        :is-page="false"
                    />
                </div>
                <IconSvg v-else :name="showDropdown ? 'chevron-up' : 'chevron-down'" width="16px" fill="#ced4da" />
            </span>
        </button>

        <transition name="fade">
            <div 
                v-if="!readonly && showDropdown" 
                class="dropdown--menu w-100"
                :class="[!shadow ? 'no-shadow' : '']"
            >
                <form v-if="showSearchInput" class="dropdown-form" :class=" { 'active': inputFocus }" @submit.prevent="">
                    <input
                        ref="searchRef" 
                        v-model="searchQuery" 
                        name="search"
                        type="text" 
                        :placeholder="`Search ${listType}...`"
                    > 
                    <span class="cursor-pointer" @click="clearSearch">
                        <icon-svg fill="rgba(194, 200, 212, 1)" :name="searchQuery ? 'close' : 'search'" width="16px" /> 
                    </span>
                </form>
    
                <div>
                    <template v-if="loading">
                        <div class="text--color-title text--sm p--25 text-center">
                            <PageSpinner class="mt--5 mb--5" size="sm" />
                        </div>
                    </template>
        
                    <template v-else>
                        <ul 
                            v-if="!readonly && options.length" 
                            class="dropdown-list mb--0" 
                            :class="[listClass, options.length < 6 ? 'h--auto' : '']"
                        >
                            <div v-if="showListTitle" class="dropdown--title">
                                All {{ listType }}
                            </div>
                            
                            <li
                                v-for="(item) in options"
                                :key="getUniqueId(item)" 
                                :class="[listItemClass]"
                                tabindex="0" 
                                class="dropdown-item dropdown--item"
                                @click="onSelect(item)" 
                                @keyup.enter="onSelect(item)"
                            >
                                <div class="flex flex__item--grow align-items-center">
                                    <slot name="prefix" :item="item" />
        
                                    <p v-if="!customText" class="dropdown--item-label">
                                        {{ computeOptionItem(item) }}
                                    </p>
                                    <slot v-else name="text" :item="item" />

                                    <slot name="suffix" :item="item" />
                                </div>
                            </li>
                        </ul>
        
                        <div v-else class="p--25 m-auto flex flex-col align-items-center w-100">
                            <IconSvg name="search" width="24px" fill="#ced4da" class="not-found-icon" />
                            <p class="text--color-title text--sm p--25 text-center">
                                No results found
                            </p>
                        </div>
                    </template>
                </div>
            </div>
        </transition>
    </div>
</template>
  
<script>  
import IconSvg from "../icons/Icon-Svg.vue";
import PageSpinner from "../loaders/PageSpinner.vue";
import { debounce } from "@/utils/helpers";

export default {
    name: "CustomDropdown",

    components: { IconSvg, PageSpinner },

    directives: {
        "click-outside": {
            beforeMount(el, binding) { // Renamed from 'bind'
                el.clickOutsideEvent = function(event) {
                    // Check if the click was outside the el and its children
                    if (!(el === event.target || el.contains(event.target))) {
                        // Call the method provided in the directive's value
                        binding.value(event);
                    }
                };
                document.addEventListener("click", el.clickOutsideEvent);
            },
            unmounted(el) { // Renamed from 'unbind'
                document.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },

    props: {
        options: { type: Array, default: () => [] },
        listType: { type: String, default: "" },
        indexLabel: { type: String, default: "" },
        disabled: { type: Boolean, default: false },
        customText: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        searchable: { type: Boolean, default: true },
        loading: { type: Boolean, default: false },
        outerLoading: { type: Boolean, default: false },
        modelValue: { type: [String, Object, Array], default: undefined },
        multiple: { type: Boolean, default: false },
        showListTitle: { type: Boolean, default: false },
        closeOnSelect: { type: Boolean, default: true },
        clearOnSelect: { type: Boolean, default: true },
        prefix: { type: Boolean, default: false },
        suffix: { type: Boolean, default: false },
        btnClasses: { type: String, default: "" },
        placeholder: { type: String, default: "" },
        listItemClass: { type: String, default: "" },
        listClass: { type: String, default: "" },
        shadow: { type: Boolean, default: true },
        fetchListOnShow: { type: Boolean, default: false },
        size: { 
            type: String, 
            default: "md",
            validator: (value) => {
                return ["sm", "md", "lg"].includes(value);
            },
        },
    },

    emits: ["update:modelValue", "select", "search", "remove", "fetch-list-show"],

    data() {
        return {
            searchQuery: "",
            reactiveSelected: this.modelValue || undefined, // string, object or array of string or object items
            showDropdown: false,
            searchRef: null,
            inputFocus: false,
        };
    },

    computed: {
        showSearchInput() {
            if (!this.searchable) {
                return false;
            }

            if (this.options.length < 5 && !this.searchQuery) {
                return false;
            }

            return true;
        },
    },

    watch: {
        modelValue(newValue) {
            this.updateDisplayValue(newValue);
        },

        searchQuery(newVal) {
            this.searchList(newVal);
            // if (newVal) {
            // } else {
            //     if (this.$refs.searchRef) {
            //         this.$refs.searchRef.focus();
            //         this.inputFocus = true;
            //     }
            // }
        },

        showDropdown(newVal) {            
            if (newVal) {
                if (this.showSearchInput) {                    
                    this.$nextTick(() => {                        
                        if (this.$refs.searchRef) {
                            this.$refs.searchRef.focus();
                            this.inputFocus = true;
                        }
                    });
                }

                if (this.fetchListOnShow) {
                    this.$emit("fetch-list-show");
                }
            }
        },
    },

    methods: {
        toggleDropdown() {
            if (this.readonly) return;
            
            this.showDropdown = !this.showDropdown;
        },

        getUniqueId(item) {
            return item._id || item[this.indexLabel] || item;
        },

        closeDropdown() {
            // console.log("run closeDropdown function");
            this.showDropdown = false;
        },

        computeOptionItem(item) { // returns a string or an object            
            if (typeof item === "string") {
                return item;
            }
            
            if (typeof item === "object") {
                if (!this.indexLabel) {
                    return undefined;
                }
                return item[this.indexLabel];
            } 

            return item;
        },

        updateDisplayValue(value) { // string, object or an array            
            if (!value) return;

            if (this.multiple && Array.isArray(this.reactiveSelected)) { // model value is an array
                this.reactiveSelected = value;
                return;
            }

            // model value is an object or string
            const selectedItem = this.options.find((val) => {     
                // string
                if ((typeof value === "string" && typeof val === "string") && val === value) {
                    return value;  // arts
                }  
                
                // object 
                if ((typeof value === "object" && typeof val === "object") && this.indexLabel) {
                    return val[this.indexLabel] === value[this.indexLabel];
                }
            });

            if (!selectedItem) {
                this.reactiveSelected = undefined;
            } else {
                this.reactiveSelected = selectedItem;
            }
        },

        onSelect(item) {
            this.$emit("select", item);

            if (this.closeOnSelect) {
                this.showDropdown = false;
            }
            
            if (this.clearOnSelect) {
                this.searchQuery = "";
            }
        },

        clearSearch() {
            this.searchQuery = "";
        },

        searchList(newVal) {                        
            debounce(() => {
                this.$emit("search", newVal);
            }, 300, "search-list");
        },
    },
};

</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.not-found-icon {
    border: 1px solid transparent;
    background: #f7f7f7;
    border-radius: 50%;
    padding: 6px 3px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        fill: #94a4bd;
    }
}
</style>